import { useInfiniteQuery } from '@tanstack/react-query';
import { api } from '@/api';
import { PaginatedBody, PaginationSearchParams, SearchQuery, SortOrderQuery } from '@/api/types';
import { NotificationType } from '@/api/apis/communication';
import { notificationsQueryKeys } from '@/queries/notification/notifications.keys';
import { DEFAULT_INFINITE_QUERY_OPTIONS } from '@/queries/utils';
import { groupNotifications, sortKeys } from './utils';

type SortedNotificationType = { [key: string]: NotificationType[] };

type NotificationResponse = {
  sortedKeys: string[];
  sortedNotifications: SortedNotificationType;
} & PaginatedBody<NotificationType>;

const fetchAndPrepareNotificationsList = async (
  params: PaginationSearchParams & SearchQuery & SortOrderQuery,
): Promise<NotificationResponse> => {
  const response = await api.communication.getNotificationsList(params);

  const sortedNotifications = groupNotifications(response.items);
  const sortedKeys = sortKeys(sortedNotifications);

  return {
    ...response,
    sortedNotifications,
    sortedKeys,
  };
};

export const notificationsQueries = {
  all: () => ({
    queryKey: notificationsQueryKeys.getList(),
    queryFn: () => fetchAndPrepareNotificationsList({ itemsPerPage: 50, pageIndex: 0 }),
  }),
  infiniteList: (searchPagination: PaginationSearchParams & SearchQuery & SortOrderQuery) => ({
    queryKey: notificationsQueryKeys.getInfiniteList(searchPagination),
    queryFn: ({ pageParam = 0 }) => fetchAndPrepareNotificationsList({ ...searchPagination, pageIndex: pageParam }),
    ...DEFAULT_INFINITE_QUERY_OPTIONS,
  }),
};

const useInfiniteNotificationsListQuery = (filters: SearchQuery & SortOrderQuery) => {
  return useInfiniteQuery({
    ...notificationsQueries.infiniteList({
      itemsPerPage: 50,
      pageIndex: 0,
      ...filters,
    }),
    select: (data) => {
      if (!data) return { items: {}, sortedKeys: [] };

      const sortedNotifications = data.pages.reduce((acc, page) => {
        page.sortedKeys.forEach((key) => {
          acc[key] = [...(acc[key] || []), ...page.sortedNotifications[key]];
        });
        return acc;
      }, {} as SortedNotificationType);

      const sortedKeys = sortKeys(sortedNotifications);

      return {
        items: sortedNotifications,
        sortedKeys,
      };
    },
  });
};

export { useInfiniteNotificationsListQuery };
